import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Save, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SmtpTab() {
  const [f, setF] = useState({
    host: "", port: 587, use_tls: true, username: "", password: "",
    from_email: "", from_name: "Fucan Personel Giyim", reply_to: "", enabled: false,
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shipment-email/settings");
      setF({ ...data, password: "" });
      setPasswordSet(!!data.password_set);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      await api.post("/shipment-email/settings", f);
      toast.success("SMTP ayarları kaydedildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const test = async () => {
    if (!testTo) { toast.error("Test için bir alıcı e-postası girin"); return; }
    try {
      setTesting(true);
      const { data } = await api.post("/shipment-email/settings/test", { to: testTo });
      toast.success(data.message || "Test maili gönderildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-4" data-testid="smtp-tab">
      <Card className="bg-slate-950/60 border-slate-800 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <Mail className="w-4 h-4" /> SMTP Sunucu Bilgileri — Sevkiyat müşteri bildirim e-postaları buradan gönderilir.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Host (ör. smtp.gmail.com)</Label>
            <Input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-host" />
          </div>
          <div>
            <Label className="text-xs">Port</Label>
            <Input type="number" value={f.port} onChange={(e) => setF({ ...f, port: Number(e.target.value) })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-port" />
          </div>
          <div>
            <Label className="text-xs">Kullanıcı Adı (SMTP username)</Label>
            <Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-username" />
          </div>
          <div>
            <Label className="text-xs">Şifre {passwordSet && <span className="text-emerald-400 text-[10px]">(kayıtlı — boş bırakırsanız korunur)</span>}</Label>
            <Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
              placeholder={passwordSet ? "•••••• (kayıtlı)" : ""}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-password" />
          </div>
          <div>
            <Label className="text-xs">Gönderen E-posta (From)</Label>
            <Input type="email" value={f.from_email} onChange={(e) => setF({ ...f, from_email: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-from-email" />
          </div>
          <div>
            <Label className="text-xs">Gönderen Adı</Label>
            <Input value={f.from_name} onChange={(e) => setF({ ...f, from_name: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-from-name" />
          </div>
          <div>
            <Label className="text-xs">Yanıt Adresi (opsiyonel)</Label>
            <Input type="email" value={f.reply_to} onChange={(e) => setF({ ...f, reply_to: e.target.value })}
              className="bg-slate-950 border-slate-700 rounded-sm mt-1" data-testid="smtp-reply-to" />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={f.use_tls} onCheckedChange={(v) => setF({ ...f, use_tls: v })} data-testid="smtp-tls" />
            <Label className="text-xs">STARTTLS Kullan (port 587 için AÇIK; 465 için KAPALI)</Label>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="smtp-enabled" />
            <Label className="text-xs">Otomatik Bildirim Aktif (kapalıysa mail gönderilmez)</Label>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="smtp-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Kaydet</>}
          </Button>
          <div className="flex-1" />
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@ornek.com"
            className="bg-slate-950 border-slate-700 rounded-sm w-56" data-testid="smtp-test-to" />
          <Button onClick={test} disabled={testing} variant="outline" className="border-slate-700 rounded-sm" data-testid="smtp-test">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Test Maili Gönder</>}
          </Button>
        </div>
      </Card>
      <Card className="bg-slate-950/40 border-slate-800 p-3 text-xs text-slate-400">
        <b className="text-slate-200">Popüler SMTP Ayarları:</b>
        <ul className="mt-1 space-y-0.5 list-disc pl-5">
          <li>Gmail: smtp.gmail.com · 587 · TLS · App password gerekli</li>
          <li>Outlook 365: smtp.office365.com · 587 · TLS</li>
          <li>Yandex: smtp.yandex.com · 465 · SSL (STARTTLS kapalı)</li>
          <li>Zoho: smtp.zoho.com · 587 · TLS</li>
        </ul>
      </Card>
    </div>
  );
}
